import type { Dispatch, SetStateAction } from "react";
import type { ProspectListResponse } from "../../api";
import { Btn, Card, I } from "../../ui";
import { ProspectRow, type ProspectRowActions } from "./ProspectListParts";
interface ProspectResultsProps {
  total: number; offset: number; pageEnd: number; pageSize: number;
  err: string | null; data: ProspectListResponse | null; busy: boolean;
  selectedId: number | null; rowBusyId: number | null;
  rowActions: Omit<ProspectRowActions, "busy">;
  onSelect: (id: number) => void;
  canPrev: boolean; canNext: boolean;
  setOffset: Dispatch<SetStateAction<number>>;
  exportSelection?: {
    ids: ReadonlySet<number>;
    onToggle: (ids: number[], checked: boolean) => void;
    onClear: () => void;
    disabled: boolean;
  };
}
export function ProspectResults({ total, offset, pageEnd, pageSize, err, data, busy, selectedId, rowBusyId, rowActions, onSelect, canPrev, canNext, setOffset, exportSelection }: ProspectResultsProps) {
  const pageIds = data?.items.map(item => item.id) ?? [];
  const selectedOnPage = pageIds.filter(id => exportSelection?.ids.has(id)).length;
  return (
      <Card
        title={`Risultati${total ? ` · ${total.toLocaleString("it-IT")}` : ""}`}
        sub={total ? `${offset + 1}-${pageEnd} di ${total.toLocaleString("it-IT")}` : "nessuna scheda"}
        noPad
      >
        {exportSelection && pageIds.length > 0 && (
          <div className="prospect-export-selection row gap-8">
            <label className="row gap-8">
              <input type="checkbox" checked={selectedOnPage === pageIds.length}
                ref={node => { if (node) node.indeterminate = selectedOnPage > 0 && selectedOnPage < pageIds.length; }}
                aria-checked={selectedOnPage > 0 && selectedOnPage < pageIds.length ? "mixed" : selectedOnPage === pageIds.length}
                disabled={exportSelection.disabled}
                onChange={event => exportSelection.onToggle(pageIds, event.target.checked)} />
              Seleziona pagina per Excel
            </label>
            <span className="muted" role="status">{exportSelection.ids.size.toLocaleString("it-IT")} selezionati tra tutte le pagine</span>
            {exportSelection.ids.size > 0 && <Btn size="sm" variant="ghost" onClick={exportSelection.onClear}>Annulla selezione</Btn>}
          </div>
        )}
        {err && <div style={{ padding: 12, color: "var(--danger)", fontSize: 12 }}>{err}</div>}
        {!err && !data && busy && (
          <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>Caricamento…</div>
        )}
        {!err && data && data.items.length === 0 && (
          <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>
            Nessuna scheda con questi filtri. Le nuove ricerche partono da Necrologi → «Cerca su SISTER».
          </div>
        )}
        {!err && data && data.items.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl prospect-table">
              <thead>
                <tr>
                  {exportSelection && <th className="prospect-export-col">Excel</th>}
                  <th className="prospect-check-col" title="Spunta le schede già guardate e lavorate"><span className="sr-only">Già lavorata</span>{I.check}</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Persona e primo immobile</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Esito</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Cosa fare</th>
                  <th style={{ textAlign: "right", padding: "8px 12px" }}>Immobili</th>
                  <th className="prospect-actions-col"><span className="sr-only">Azioni</span></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <ProspectRow
                    key={p.id}
                    p={p}
                    selected={p.id === selectedId}
                    onClick={() => onSelect(p.id)}
                    actions={{ ...rowActions, busy: rowBusyId === p.id }}
                    exportSelection={exportSelection ? {
                      checked: exportSelection.ids.has(p.id), disabled: exportSelection.disabled,
                      onChange: checked => exportSelection.onToggle([p.id], checked),
                    } : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (total > pageSize || offset > 0) && (
          <div
            className="row gap-8"
            style={{ padding: 12, borderTop: "1px solid var(--line)", justifyContent: "flex-end" }}
          >
            <Btn
              size="sm"
              variant="ghost"
              disabled={!canPrev || busy}
              onClick={() => setOffset(Math.max(0, offset - pageSize))}
            >
              ← Precedente
            </Btn>
            <span className="mono muted" style={{ alignSelf: "center", fontSize: 11 }}>
              pagina {Math.floor(offset / pageSize) + 1} / {Math.ceil(total / pageSize)}
            </span>
            <Btn
              size="sm"
              variant="ghost"
              disabled={!canNext || busy}
              onClick={() => setOffset(offset + pageSize)}
            >
              Successiva →
            </Btn>
          </div>
        )}
      </Card>
  );
}
